import { Link } from "react-router-dom";
import { Building2, Eye, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useWhatsApp } from "@/lib/siteSettings";
import {
  formatApartmentType, formatBuildingAge, formatPrice,
  type BuildingAge, type CurrencyCode, type PublicListing,
} from "@/lib/listings";

type Props = { listing: PublicListing };

export const ListingCard = ({ listing: l }: Props) => {
  const { t } = useTranslation();
  const { buildWaUrl } = useWhatsApp();
  const img = l.listing_images?.[0]?.image_url;
  const currency = (l.currency ?? "USD") as CurrencyCode;
  const eligible = Boolean((l as { eligible_for_citizenship?: boolean }).eligible_for_citizenship);
  const buildingAge = (l as { building_age?: string }).building_age as BuildingAge | undefined;
  const viewCount = Number((l as { view_count?: number | null }).view_count ?? 0);

  const metaParts: string[] = [];
  if (l.apartment_type) metaParts.push(formatApartmentType(l.apartment_type));
  if (l.furnished) metaParts.push(t(`furnished.${l.furnished}`));
  if (buildingAge) metaParts.push(formatBuildingAge(buildingAge));

  const propertyId = (l as { property_id?: string | null }).property_id ?? "";
  const waMsg = t("listing.card.whatsappMessage", { title: l.title, area: l.area, propertyId });
  const waUrl = buildWaUrl(waMsg);
  const priceShown = Number(l.price) > 0;

  return (
    <article className="group overflow-hidden rounded-xl border border-border/40 bg-card shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <Link to={`/listings/${l.id}`} aria-label={l.title ?? "Listing"} className="block">
        <div className="relative aspect-[16/9] overflow-hidden bg-muted">
          {img ? (
            <img
              src={img}
              alt={l.title ?? ""}
              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
              loading="lazy"
            />
          ) : (
            <div className="grid h-full place-items-center text-muted-foreground">
              <Building2 className="h-10 w-10" />
            </div>
          )}
          {/* Bottom gradient for badge legibility */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent" />

          {/* Listing type — top end (RTL-aware) */}
          <span className="absolute end-2 top-2 rounded-full bg-primary px-2 py-1 text-[11px] font-bold text-primary-foreground shadow-sm">
            {l.listing_type === "rent" ? t("listing.card.badge.rent") : t("listing.card.badge.sale")}
          </span>

          {/* Citizenship — top start */}
          {eligible && (
            <span className="absolute start-2 top-2 rounded-full bg-amber-500/90 px-2 py-1 text-[11px] font-bold text-white shadow-sm">
              🇹🇷 {t("listing.card.badge.citizenship")}
            </span>
          )}

          {/* Property ID — bottom start */}
          {propertyId && (
            <span
              className="absolute bottom-2 start-2 rounded-md bg-black/50 px-2 py-1 font-mono text-[11px] text-white/85 backdrop-blur-sm"
              dir="ltr"
            >
              {propertyId}
            </span>
          )}

          {/* Status overlay */}
          {l.status && l.status !== "available" && (
            <span className="absolute bottom-2 end-2 rounded-md bg-destructive/90 px-2 py-1 text-[11px] font-bold text-destructive-foreground">
              {t(`listingStatus.${l.status}`)}
            </span>
          )}
        </div>
      </Link>

      <div className="flex flex-col gap-1 px-3 pb-3 pt-2">
        <Link to={`/listings/${l.id}`} className="flex items-start justify-between gap-2">
          <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            <span className="truncate max-w-[8rem]">{l.area}</span>
          </span>
          <h3 className="line-clamp-1 min-w-0 text-sm font-semibold text-foreground">{l.title}</h3>
        </Link>

        {metaParts.length > 0 && (
          <p className="truncate text-xs text-muted-foreground">
            {metaParts.join(" • ")}
          </p>
        )}

        <div className="mt-0.5 flex items-center justify-between gap-2">
          <p className="text-base font-bold text-primary">
            {priceShown ? formatPrice(Number(l.price), currency) : t("listing.card.priceOnContact")}
          </p>
          {viewCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Eye className="h-3.5 w-3.5" />
              {viewCount}
            </span>
          )}
        </div>

        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition hover:brightness-110"
        >
          {t("listing.card.inquireNow")}
        </a>
      </div>
    </article>
  );
};

export default ListingCard;
